import React from "react";
import WebsiteLayout from "./WebsiteLayout";
import { useScrollReveal } from "./useAnimations";
import { Shield, Lock, Eye, Server, UserCheck, KeyRound, Check } from "lucide-react";

const SECURITY_GRID = [
  { icon: <Lock size={24} />, label: "Encrypted", value: "AES-256" },
  { icon: <UserCheck size={24} />, label: "Access Control", value: "RBAC" },
  { icon: <Shield size={24} />, label: "Compliant", value: "ISO 27001" },
  { icon: <Eye size={24} />, label: "Monitored", value: "24/7" },
  { icon: <Server size={24} />, label: "Redundant", value: "Multi-region" },
  { icon: <KeyRound size={24} />, label: "Authentication", value: "MFA" },
];

const SECTIONS = [
  { icon: <Lock size={28} />, title: "Data Security",
    desc: "All data within Tychi is protected using industry-standard encryption protocols. Data is encrypted both at rest and in transit, ensuring sensitive financial information remains secure at all times.",
    points: ["AES-256 encryption for all stored financial data", "TLS 1.3 for all data transmission", "Database-level encryption with key rotation", "Secure credential storage with hashing and salting"] },
  { icon: <UserCheck size={28} />, title: "Access Control",
    desc: "Comprehensive role-based access control ensures users can only access data and functions appropriate to their role. Multi-level permission structures support complex organizational hierarchies.",
    points: ["Role-based access control with granular permissions", "Multi-factor authentication support", "Session management with automatic timeout", "IP-based access restrictions for sensitive operations"] },
  { icon: <KeyRound size={28} />, title: "Encryption Standards",
    desc: "Enterprise-grade encryption standards across all platform components. Every data touchpoint is protected with cryptographic controls meeting institutional requirements.",
    points: ["RSA-2048+ for asymmetric key operations", "SHA-256 for data integrity verification", "Secure key management with hardware-backed storage", "Regular cryptographic protocol reviews"] },
  { icon: <Server size={28} />, title: "System Integrity",
    desc: "Continuous system integrity through automated monitoring, AI-driven anomaly detection, and comprehensive logging. Every operation is tracked and auditable.",
    points: ["Real-time system health monitoring and alerting", "AI-driven anomaly detection for unusual activity", "Complete audit trail for all system operations", "Regular integrity checks on all financial data"] },
  { icon: <Eye size={28} />, title: "Operational Security",
    desc: "Operational security practices ensure the platform maintains its security posture through regular assessments and controlled deployment processes.",
    points: ["Regular security assessments and penetration testing", "Controlled deployment pipelines with security gates", "Incident response procedures with defined escalation", "Continuous security training and awareness"] },
];

export default function SecurityPage() {
  useScrollReveal();
  return (
    <WebsiteLayout>
      <div className="w-banner-dark">
        <section className="w-banner w-reveal" data-testid="security-banner" style={{ paddingBottom: "4rem" }}>
          <div className="w-label">Security</div>
          <h1 className="w-title" style={{ fontSize: "clamp(2.5rem, 5vw, 3.5rem)" }}>
            Institutional-Grade <strong>Security</strong>
          </h1>
          <p className="w-subtitle" style={{ maxWidth: 560 }}>
            Built with security as a foundational principle. Every layer implements controls 
            designed to protect sensitive financial data and ensure institutional trust.
          </p>
        </section>
      </div>

      <section className="w-section" style={{ paddingTop: "3rem" }}>
        <div className="w-arch w-reveal-scale">
          {SECURITY_GRID.map((s, i) => (
            <div key={i} className="w-arch-item">
              <div style={{ color: "var(--w-blue)" }}>{s.icon}</div>
              <h4>{s.label}</h4><p>{s.value}</p>
            </div>
          ))}
        </div>
      </section>

      {SECTIONS.map((s, i) => (
        <section key={i} className={i % 2 === 0 ? "w-section-full w-section-surface" : "w-section"}>
          <div className={i % 2 === 0 ? "w-section" : ""} style={i % 2 === 0 ? { paddingTop: "6rem", paddingBottom: "6rem" } : {}}>
            <div className={`w-split ${i % 2 === 1 ? "reverse" : ""}`}>
              <div className={i % 2 === 0 ? "w-reveal-left" : "w-reveal-right"}>
                <div className="w-label">{s.title}</div>
                <h2 className="w-title" style={{ fontSize: "clamp(1.75rem, 3vw, 2.25rem)" }}>{s.title}</h2>
                <p style={{ color: "var(--w-text-muted)", lineHeight: 1.75, marginBottom: "1.5rem" }}>{s.desc}</p>
                <ul className="w-features">
                  {s.points.map((p, j) => <li key={j}><span className="check-icon"><Check size={12} /></span> {p}</li>)}
                </ul>
              </div>
              <div className={`${i % 2 === 0 ? "w-reveal-right" : "w-reveal-left"} w-delay-2`} style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                <div style={{ width: 180, height: 180, borderRadius: "50%", border: "1px solid var(--w-border)", background: "var(--w-surface)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <div style={{ color: "var(--w-blue)" }}>{React.cloneElement(s.icon, { size: 56 })}</div>
                </div>
              </div>
            </div>
          </div>
        </section>
      ))}
    </WebsiteLayout>
  );
}
